import * as React from "react";
import { Navigate, createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BarChart3,
  Globe,
  Smartphone,
  Link2,
  Unplug,
  ExternalLink,
  AlertCircle,
  MousePointerClick,
  ShoppingCart,
  BadgeCheck,
  TrendingUp,
  Package,
} from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "@/lib/toast";
import {
  getMyGA4Status,
  getMyGA4Stats,
  disconnectMyGA4,
} from "@/utils/ga4Connection.functions";
import { getMyLinkStats } from "@/utils/ambassador.functions";

export const Route = createFileRoute("/account/analytics")({
  component: MyAnalyticsPage,
  validateSearch: (s: Record<string, unknown>) => ({
    error: typeof s.error === "string" ? s.error : undefined,
  }),
  head: () => ({
    meta: [
      { title: DICTS.en["account.analytics.meta_title"] ?? "My Analytics — Lecmo" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const RANGE_OPTIONS = [
  { value: 7, labelAr: "٧ أيّام", labelEn: "7d" },
  { value: 30, labelAr: "٣٠ يوماً", labelEn: "30d" },
  { value: 90, labelAr: "٩٠ يوماً", labelEn: "90d" },
] as const;

type Days = 7 | 30 | 90;

function MyAnalyticsPage() {
  const { user, loading } = useAuth();
  const { dir, locale } = useLocale();
  const isAr = locale === "ar";
  const { error: errorParam } = useSearch({ from: "/account/analytics" });
  const [days, setDays] = React.useState<Days>(30);

  const fetchLinkStats = useServerFn(getMyLinkStats);

  const linkQ = useQuery({
    queryKey: ["my-link-stats", days, user?.id ?? null],
    queryFn: () => fetchLinkStats({ data: { days } }),
    enabled: !!user,
    staleTime: 60_000,
  });

  if (!loading && !user) {
    return <Navigate to="/auth" search={{ redirect: "/account/analytics", mode: "signin" }} replace />;
  }

  if (loading || !user) {
    return (
      <SiteShell>
        <div className="flex min-h-[60vh] items-center justify-center">
          <p className="text-sm text-muted-foreground">{isAr ? "تحميل…" : "Loading…"}</p>
        </div>
      </SiteShell>
    );
  }

  const stats = linkQ.data;
  const clicks = stats?.totals.clicks ?? 0;
  const purchases = stats?.totals.purchases ?? 0;
  const conv = clicks > 0 ? Math.round((purchases / clicks) * 1000) / 10 : 0;
  const hasData =
    !!stats && (clicks > 0 || purchases > 0 || (stats.totals.addToCart ?? 0) > 0);

  return (
    <SiteShell>
      <div className="mx-auto max-w-4xl px-4 pb-24 pt-6 md:px-8 md:pt-8" dir={dir}>
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <h1 className="t-h2 text-foreground">{isAr ? "إحصاءاتي" : "My Analytics"}</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {isAr
            ? "أرقام رابطك الشخصيّ مباشرةً — بلا أيّ إعداد أو ربط."
            : "Your personal link's numbers — no setup or connection needed."}
        </p>

        {errorParam ? (
          <Card className="mt-4 flex items-start gap-2 border-destructive/40 bg-destructive/5 p-3 text-sm">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div className="text-destructive">
              {isAr ? "خطأ في الربط: " : "Connection error: "}
              <span className="font-mono">{errorParam}</span>
            </div>
          </Card>
        ) : null}

        {/* مبدّل الفترة + الكود */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          {stats?.code ? (
            <p className="text-xs text-muted-foreground">
              {isAr ? "كودك: " : "Your code: "}
              <span className="font-mono text-foreground">{stats.code}</span>
            </p>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setDays(r.value as Days)}
                className={`min-h-[36px] rounded-md border px-3 py-1.5 text-xs transition ${
                  days === r.value
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {isAr ? r.labelAr : r.labelEn}
              </button>
            ))}
          </div>
        </div>

        {linkQ.isLoading ? (
          <p className="mt-6 text-sm text-muted-foreground">
            {isAr ? "جلب الأرقام…" : "Loading numbers…"}
          </p>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-5">
              <Kpi
                label={isAr ? "النقرات" : "Clicks"}
                value={clicks}
                icon={<MousePointerClick className="h-3.5 w-3.5" />}
              />
              <Kpi
                label={isAr ? "إضافة للسلّة" : "Add to cart"}
                value={stats?.totals.addToCart ?? 0}
                icon={<ShoppingCart className="h-3.5 w-3.5" />}
              />
              <Kpi
                label={isAr ? "أضاف وترك" : "Added, left"}
                value={Math.max(0, (stats?.totals.addToCart ?? 0) - purchases)}
                icon={<ShoppingCart className="h-3.5 w-3.5" />}
              />
              <Kpi
                label={isAr ? "المشتريات" : "Purchases"}
                value={purchases}
                icon={<BadgeCheck className="h-3.5 w-3.5" />}
              />
              <Kpi
                label={isAr ? "نسبة التحويل" : "Conversion"}
                value={`${conv}%`}
                icon={<TrendingUp className="h-3.5 w-3.5" />}
              />
            </div>

            {!hasData ? (
              <Card className="mt-6 p-6 text-center">
                <p className="text-sm text-foreground">
                  {isAr
                    ? "لا نقرات بعد — شارك رابطك ليبدأ العدّ."
                    : "No clicks yet — share your link and the counting begins."}
                </p>
                <Button asChild className="mt-4" size="lg">
                  <Link to="/account/media">
                    {isAr ? "مكتبة المحتوى" : "Content library"}
                  </Link>
                </Button>
              </Card>
            ) : (
              <>
                <DailyChart daily={stats?.daily ?? []} isAr={isAr} />

                <div className="grid gap-4 md:grid-cols-2">
                  <Section
                    title={isAr ? "بحسب الدولة" : "By country"}
                    icon={<Globe className="h-4 w-4" />}
                  >
                    <RowList
                      rows={(stats?.byCountry ?? []).map((c) => ({
                        key: c.country,
                        label: c.country,
                        value: c.clicks,
                      }))}
                      isAr={isAr}
                    />
                  </Section>

                  <Section
                    title={isAr ? "بحسب الجهاز" : "By device"}
                    icon={<Smartphone className="h-4 w-4" />}
                  >
                    <RowList
                      rows={(stats?.byDevice ?? []).map((d) => ({
                        key: d.device,
                        label: d.device,
                        value: d.clicks,
                      }))}
                      isAr={isAr}
                      capitalize
                    />
                  </Section>
                </div>

                <Section
                  title={isAr ? "أكثر الصفحات نقراً" : "Most clicked pages"}
                  icon={<Package className="h-4 w-4" />}
                >
                  <RowList
                    rows={(stats?.topPaths ?? []).map((p) => ({
                      key: p.path,
                      label: p.path,
                      value: p.clicks,
                    }))}
                    isAr={isAr}
                    mono
                  />
                </Section>
              </>
            )}
          </>
        )}

        {/* قسم متقدّم: ربط GA4 */}
        <details className="group mt-10 rounded-none border border-border bg-card p-4">
          <summary className="cursor-pointer list-none text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {isAr
              ? "للمتقدّمين: اربط Google Analytics"
              : "Advanced: connect Google Analytics"}
          </summary>
          <div className="mt-4">
            <Ga4Section isAr={isAr} />
          </div>
        </details>

        <div className="mt-8">
          <Link to="/account" className="text-xs text-muted-foreground hover:text-foreground">
            ← {isAr ? "حسابي" : "My Account"}
          </Link>
        </div>
      </div>
    </SiteShell>
  );
}

function DailyChart({ daily, isAr }: { daily: { date: string; clicks: number }[]; isAr: boolean }) {
  const max = daily.reduce((m, d) => Math.max(m, d.clicks), 0);
  return (
    <Card className="mt-6 p-4">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {isAr ? "النقرات اليوميّة" : "Daily clicks"}
      </h3>
      <div className="flex h-28 items-end gap-[3px]" dir="ltr">
        {daily.map((d) => (
          <div
            key={d.date}
            title={`${d.date} · ${d.clicks}`}
            className="flex-1 rounded-t-sm bg-primary/70"
            style={{ height: max > 0 ? `${Math.max(2, (d.clicks / max) * 100)}%` : "2%" }}
          />
        ))}
      </div>
      {daily.length > 1 ? (
        <div className="mt-2 flex justify-between font-mono text-[10px] text-muted-foreground" dir="ltr">
          <span>{daily[0]?.date}</span>
          <span>{daily[daily.length - 1]?.date}</span>
        </div>
      ) : null}
    </Card>
  );
}

function RowList({
  rows,
  isAr,
  mono,
  capitalize,
}: {
  rows: { key: string; label: string; value: number }[];
  isAr: boolean;
  mono?: boolean;
  capitalize?: boolean;
}) {
  if (rows.length === 0) {
    return <p className="text-xs text-muted-foreground">{isAr ? "لا بيانات" : "No data"}</p>;
  }
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.key} className="flex items-center justify-between gap-3 text-sm">
          <span
            className={`min-w-0 truncate text-foreground ${mono ? "font-mono text-xs" : ""} ${
              capitalize ? "capitalize" : ""
            }`}
          >
            {r.label}
          </span>
          <span className="shrink-0 tabular-nums text-muted-foreground">{r.value}</span>
        </li>
      ))}
    </ul>
  );
}

/** بطاقة ربط GA4 (اختياريّة، للمتقدّمين). */
function Ga4Section({ isAr }: { isAr: boolean }) {
  const { user } = useAuth();
  const [days, setDays] = React.useState<Days>(30);
  const fetchStatus = useServerFn(getMyGA4Status);
  const fetchStats = useServerFn(getMyGA4Stats);
  const doDisconnect = useServerFn(disconnectMyGA4);
  const qc = useQueryClient();

  const statusQ = useQuery({
    queryKey: ["ga4-status", user?.id ?? null],
    queryFn: () => fetchStatus(),
    enabled: !!user,
    staleTime: 30_000,
  });
  const isConnected = !!statusQ.data?.connection;

  const statsQ = useQuery({
    queryKey: ["ga4-stats", days, user?.id ?? null],
    queryFn: () => fetchStats({ data: { days } }),
    enabled: !!user && isConnected,
    staleTime: 60_000,
  });

  const disconnectMut = useMutation({
    mutationFn: () => doDisconnect(),
    onSuccess: () => {
      toast.success(isAr ? "تمّ فكّ الربط" : "Disconnected");
      qc.invalidateQueries({ queryKey: ["ga4-status"] });
      qc.invalidateQueries({ queryKey: ["ga4-stats"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : String(e)),
  });

  if (!isConnected) {
    return (
      <>
        <p className="text-sm text-muted-foreground">
          {isAr
            ? "ربط GA4 اختياريّ تماماً — يفيدك فقط إن كنت تشغّل إعلانات وتريد تحليلاً أعمق داخل Google Analytics."
            : "Connecting GA4 is fully optional — useful only if you run ads and want deeper Google Analytics insight."}
        </p>
        <Button asChild className="mt-4" size="sm">
          <a href="/api/public/ga4-oauth-start">
            <Link2 className="me-2 h-4 w-4" />
            {isAr ? "اربط حسابي" : "Connect my account"}
          </a>
        </Button>
        <p className="mt-3 text-xs text-muted-foreground">
          {isAr
            ? "نطلب صلاحيّة قراءة فقط (analytics.readonly). نخزّن رمز التجديد مشفَّراً ويمكنك فكّ الربط في أيّ وقت."
            : "We request read-only access (analytics.readonly). Refresh token is stored encrypted; disconnect anytime."}
        </p>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm">
          <div className="font-medium text-foreground">
            {statusQ.data?.connection?.property_display_name ??
              statusQ.data?.connection?.property_id}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {statusQ.data?.connection?.google_email}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setDays(r.value as Days)}
              className={`min-h-[36px] rounded-md border px-3 py-1.5 text-xs transition ${
                days === r.value
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {isAr ? r.labelAr : r.labelEn}
            </button>
          ))}
        </div>
      </div>

      {statsQ.isLoading ? (
        <p className="mt-4 text-sm text-muted-foreground">
          {isAr ? "جلب الإحصاءات…" : "Loading stats…"}
        </p>
      ) : statsQ.isError ? (
        <Card className="mt-4 border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {statsQ.error instanceof Error ? statsQ.error.message : String(statsQ.error)}
        </Card>
      ) : statsQ.data?.stats ? (
        <Ga4StatsView
          stats={statsQ.data.stats}
          ambassadorCode={statsQ.data.ambassadorCode}
          isAr={isAr}
        />
      ) : null}

      <div className="mt-6 flex items-center justify-between">
        <a
          href={`https://analytics.google.com/analytics/web/#/p${statusQ.data?.connection?.property_id}/reports`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ExternalLink className="h-3 w-3" />
          {isAr ? "افتح GA4" : "Open GA4"}
        </a>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => disconnectMut.mutate()}
          disabled={disconnectMut.isPending}
          className="text-destructive hover:text-destructive"
        >
          <Unplug className="me-1.5 h-4 w-4" />
          {isAr ? "فكّ الربط" : "Disconnect"}
        </Button>
      </div>
    </>
  );
}

function Ga4StatsView({
  stats,
  ambassadorCode,
  isAr,
}: {
  stats: NonNullable<Awaited<ReturnType<typeof getMyGA4Stats>>["stats"]>;
  ambassadorCode: string | null | undefined;
  isAr: boolean;
}) {
  const avgMin = Math.round(stats.totals.averageSessionDuration / 6) / 10;
  return (
    <>
      {!stats.filteredByAmbassadorCode ? (
        <Card className="mt-4 border-yellow-500/40 bg-yellow-500/5 p-3 text-xs text-yellow-700 dark:text-yellow-400">
          {isAr
            ? "ملاحظة: الأرقام تعرض كامل property — أضف Custom Dimension باسم ambassador_code في GA4 ليصبح التصفية بكودك فعّالة."
            : "Note: numbers show the whole property — add a Custom Dimension named ambassador_code in GA4 to filter by your code."}
        </Card>
      ) : ambassadorCode ? (
        <p className="mt-3 text-xs text-muted-foreground">
          {isAr ? "مفلتر بكودك: " : "Filtered by your code: "}
          <span className="font-mono text-foreground">{ambassadorCode}</span>
        </p>
      ) : null}

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label={isAr ? "الزوّار" : "Users"} value={stats.totals.activeUsers} />
        <Kpi label={isAr ? "الجلسات" : "Sessions"} value={stats.totals.sessions} />
        <Kpi label={isAr ? "الصفحات" : "Page views"} value={stats.totals.screenPageViews} />
        <Kpi label={isAr ? "متوسّط الجلسة" : "Avg. session"} value={`${avgMin}m`} />
      </div>

      <Section title={isAr ? "بحسب الدولة" : "By country"} icon={<Globe className="h-4 w-4" />}>
        <RowList
          rows={stats.byCountry.slice(0, 8).map((c) => ({
            key: c.country,
            label: c.country,
            value: c.sessions,
          }))}
          isAr={isAr}
        />
      </Section>

      <Section title={isAr ? "بحسب الجهاز" : "By device"} icon={<Smartphone className="h-4 w-4" />}>
        <RowList
          rows={stats.byDevice.map((d) => ({
            key: d.device,
            label: d.device,
            value: d.sessions,
          }))}
          isAr={isAr}
          capitalize
        />
      </Section>
    </>
  );
}

function Kpi({
  label,
  value,
  icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <Card className="p-3">
      <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</div>
    </Card>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="mt-4 p-4">
      <div className="mb-3 flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      {children}
    </Card>
  );
}
